import locale from "@bonliva-crm/locale";
import { ErrorCode } from "@bonliva-crm/common";
import { SMS_MAX_GSM7_LENGTH, validateSmsText } from "@bonliva-crm/shared";

export type SmsTextHint = {
  valid: boolean;
  /** Localized reason the text cannot be sent, or null when it can. */
  error: string | null;
  /** `120 / 306`, counted in GSM-7 septets like the backend does. */
  counter: string;
};

type SmsTextProblem = {
  invalidCharacters: string[];
  length: number;
  maxLength: number;
};

/** Names each offending character, quoted, so a lone `’` or `–` is visible. */
function smsTextErrorMessage({ invalidCharacters, length, maxLength }: SmsTextProblem) {
  const errors: string[] = [];

  if (invalidCharacters.length > 0) {
    errors.push(
      locale.smsInvalidCharacters(invalidCharacters.map((c) => `"${c}"`).join(", "))
    );
  }
  if (length > maxLength) {
    errors.push(locale.smsTooLong(length, maxLength));
  }

  return errors.length > 0 ? errors.join(" ") : null;
}

/**
 * Same rule the API enforces in `SMSService.sendOne` (CRMDEV-7459): GSM-7
 * characters only, at most 306 septets. Checked here so the user sees what to
 * fix before sending instead of getting the API's rejection.
 */
export function getSmsTextHint(
  text: string | null | undefined,
  maxLength = SMS_MAX_GSM7_LENGTH
): SmsTextHint {
  const validation = validateSmsText(text ?? "", maxLength);

  return {
    valid: validation.valid,
    error: smsTextErrorMessage(validation),
    counter: `${validation.length} / ${validation.maxLength}`
  };
}

/** Helper text for an SMS input: the counter, then what is wrong if anything. */
export function smsHelperText(hint: SmsTextHint): string {
  return hint.error ? `${hint.counter} – ${hint.error}` : hint.counter;
}

/**
 * The API's SMS rejection (`SMS_TEXT_INVALID`) worded in Swedish, naming the
 * characters it refused. Null for any other error, so callers keep their own
 * fallback.
 */
export function translateSmsTextError(error: unknown): string | null {
  const graphQLErrors = (
    error as {
      graphQLErrors?: {
        extensions?: { code?: string; details?: Partial<SmsTextProblem> };
      }[];
    }
  )?.graphQLErrors;

  const extensions = graphQLErrors?.find(
    (e) => e.extensions?.code === ErrorCode.SmsTextInvalid
  )?.extensions;

  if (!extensions) {
    return null;
  }

  const details = extensions.details ?? {};
  return (
    smsTextErrorMessage({
      invalidCharacters: details.invalidCharacters ?? [],
      length: details.length ?? 0,
      maxLength: details.maxLength ?? SMS_MAX_GSM7_LENGTH
    }) ?? locale.smsInvalidText
  );
}
